import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, AlertCircle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import TariffBreadcrumbs from "@/components/tarif/TariffBreadcrumbs";
import FAQSection from "@/components/tarif/FAQSection";
import DocumentDownloads from "@/components/tarif/DocumentDownloads";
import ThreeStepsProtection from "@/components/tarif/ThreeStepsProtection";
import TimelineSection from "@/components/tarif/TimelineSection";
import StickyCTA from "@/components/tarif/StickyCTA";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/layout/HeroSection";
import { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import { GlowCard } from "@/components/shared/GlowCard";
import { InteractiveCard } from "@/components/ui/interactive-card";
import ContactForm from "@/components/ContactForm";
import tarifHero from "@/assets/tarif-hero.jpg";
import { 
  getInsuranceProductSchema, 
  getTariffServiceSchema,
  getCoverageComparisonSchema,
  getInsuranceConditionsTableSchema,
  getTargetGroupsSchema,
  getCaseStudiesSchema,
  getFAQPageSchema
} from "@/utils/seoSchemas";

interface HeroFeature {
  icon: LucideIcon;
  title: string;
  subtitle: string;
}

interface CoverageArea {
  icon: LucideIcon;
  title: string;
  items: string[];
}

interface TargetGroup {
  icon: LucideIcon;
  title: string;
  description: string;
  benefits: string[];
  recommendation: string;
}

interface InsuranceCondition {
  icon: LucideIcon;
  title: string;
  description: string | string[];
}

interface FAQ {
  question: string;
  answer: string;
}

interface Document {
  title: string;
  type: string;
  url: string;
}

interface CaseStudy {
  title: string;
  description: string;
  outcome: string;
  damage: string;
}

interface BaseTariffPageProps {
  // Company Branding
  companyName: string;
  companyColor: string;
  
  // SEO & Meta
  pageTitle: string;
  metaDescription: string;
  metaKeywords: string;
  canonicalUrl: string;
  
  // Hero Section
  heroLabel: string;
  heroLabelIcon?: LucideIcon;
  heroTitle: string | React.ReactNode;
  heroDescription: string;
  heroFeatures: HeroFeature[];
  
  // Intro Alert
  alertTitle: string;
  alertDescription: string;
  
  // Coverage Areas (4 Karten)
  coverageAreas: CoverageArea[];
  
  // Target Groups (4 Gruppen)
  targetGroups: TargetGroup[];
  
  // Insurance Conditions (6 Items)
  insuranceConditions: InsuranceCondition[];
  
  // FAQ Section
  faqs: FAQ[];
  
  // Document Downloads (2 Dokumente)
  documents: Document[];
  
  // Case Studies (Optional)
  caseStudies?: CaseStudy[];
  
  // Product Schema (Optional - für Bewertungen)
  productRating?: {
    value: number;
    count: number;
  };
  priceRange?: string;
}

export const BaseTariffPage = ({
  companyName,
  companyColor,
  pageTitle,
  metaDescription,
  metaKeywords,
  canonicalUrl,
  heroLabel,
  heroLabelIcon,
  heroTitle,
  heroDescription,
  heroFeatures,
  alertTitle,
  alertDescription,
  coverageAreas,
  targetGroups,
  insuranceConditions,
  faqs,
  documents,
  caseStudies,
  productRating,
  priceRange,
}: BaseTariffPageProps) => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://praxis-versichert.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tarifwissen",
        "item": "https://praxis-versichert.de/tarifwissen"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": `${companyName} Cyberversicherung`
      }
    ]
  };

  // Structured Data Schemas
  const productSchema = getInsuranceProductSchema({
    name: `${companyName} Cyberversicherung für Arztpraxen`,
    description: metaDescription,
    url: canonicalUrl,
    brand: companyName,
    rating: productRating,
    priceRange: priceRange,
    image: "https://praxis-versichert.de/logo.png"
  });

  const serviceSchema = getTariffServiceSchema({
    companyName,
    serviceType: "Cyberversicherung für Arztpraxen",
    description: metaDescription,
    priceRange: priceRange,
    url: canonicalUrl
  });

  // Coverage Areas Schema
  const coverageSchema = getCoverageComparisonSchema({
    companyName,
    coverageAreas: coverageAreas.map(area => ({
      title: area.title,
      items: area.items
    }))
  });

  // Insurance Conditions Schema
  const conditionsSchema = getInsuranceConditionsTableSchema({
    companyName,
    conditions: insuranceConditions.map(condition => ({
      title: condition.title,
      description: condition.description
    }))
  });

  // Target Groups Schema
  const targetGroupsSchema = getTargetGroupsSchema({
    companyName,
    targetGroups: targetGroups.map(group => ({
      title: group.title,
      description: group.description,
      benefits: group.benefits,
      recommendation: group.recommendation
    }))
  });

  // Case Studies Schema (if provided)
  const caseStudiesSchema = caseStudies ? getCaseStudiesSchema({
    companyName,
    caseStudies
  }) : null;

  // FAQ Schema
  const faqSchema = getFAQPageSchema(faqs);

  const schemas = [
    breadcrumbSchema, 
    productSchema, 
    serviceSchema, 
    coverageSchema, 
    conditionsSchema, 
    targetGroupsSchema,
    faqSchema,
    ...(caseStudiesSchema ? [caseStudiesSchema] : [])
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      <StructuredData data={schemas} />
      
      <Navigation />
        
          <main className="flex-grow">
            {/* Hero Section with fade-bottom */}
            <HeroSection
              image={tarifHero}
              alt={`${companyName} Cyberversicherung für Arztpraxen - Professioneller IT-Schutz und umfassende Absicherung gegen digitale Bedrohungen`}
              title={`${companyName} Cyberversicherung – Professioneller Schutz für medizinische Einrichtungen`}
              gradientPreset="none"
              imageOpacity={30}
              overlayColor="background"
              className="pt-20 sm:pt-24 pb-12 sm:pb-16 lg:pb-20"
            >
              <div itemScope itemType="https://schema.org/Service">
                <meta itemProp="serviceType" content="Cyberversicherung" />
                <meta itemProp="provider" content="Praxis Versichert" />
                <meta itemProp="areaServed" content="Deutschland" />
                
                <TariffBreadcrumbs companyName={companyName} />
                
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
                  <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 bg-card/80 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-border/50 mb-4 sm:mb-6">
                      {heroLabelIcon && (() => {
                        const Icon = heroLabelIcon;
                        return <Icon className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: companyColor }} />;
                      })()}
                      <span className="text-[10px] sm:text-xs font-medium" style={{ color: companyColor }}>
                        {heroLabel}
                      </span>
                    </div>
                    
                    <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 lg:mb-6 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent" itemProp="name">
                      {heroTitle}
                    </h1>
                    
                    <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto" itemProp="description">
                      {heroDescription}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12">
                      <Button 
                        asChild 
                        size="lg" 
                        className="w-full sm:w-auto text-sm sm:text-base group shadow-lg hover:shadow-xl transition-all duration-300 hover:opacity-90"
                        style={{ 
                          backgroundColor: companyColor,
                          color: '#FFFFFF'
                        }}
                      >
                        <Link to="/termin-buchen">
                          Jetzt beraten lassen
                          <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                      <Button 
                        asChild 
                        variant="outline" 
                        size="lg"
                        className="w-full sm:w-auto text-sm sm:text-base border-2
                          bg-transparent
                          hover:!bg-transparent
                          hover:!border-opacity-80
                          hover:!shadow-[0_0_15px_rgba(0,0,0,0.3)]
                          transition-all duration-300"
                        style={{ 
                          borderColor: companyColor,
                          color: companyColor
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.borderColor = companyColor;
                          target.style.color = companyColor;
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget as HTMLElement;
                          target.style.borderColor = companyColor;
                          target.style.color = companyColor;
                        }}
                      >
                        <Link to="/tarifwissen">Alle Tarife vergleichen</Link>
                      </Button>
                    </div>

                    {/* Key Features with InteractiveCard */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                      {heroFeatures.map((feature, index) => (
                        <InteractiveCard 
                          key={index}
                          className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 border-border/50"
                          enablePulse={true}
                        >
                          <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: companyColor }} />
                          <div className="text-left">
                            <div className="font-semibold text-xs sm:text-sm">{feature.title}</div>
                            <div className="text-[10px] sm:text-xs text-muted-foreground">{feature.subtitle}</div>
                          </div>
                        </InteractiveCard>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </HeroSection>

            {/* Intro Alert */}
            <section className="py-8 sm:py-12">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <Alert className="max-w-4xl mx-auto border-l-4" style={{ borderLeftColor: companyColor }}>
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: companyColor }} />
                  <AlertDescription className="text-sm sm:text-base ml-2">
                    <strong>{alertTitle}</strong> {alertDescription}
                  </AlertDescription>
                </Alert>
              </div>
            </section>

            {/* Coverage Areas with GlowCard */}
            <section className="py-16 sm:py-20 lg:py-24 relative">
              <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/[0.02] to-background" />
              <ScrollAnimatedSection>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                  <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                      Umfassender Versicherungsschutz
                    </h2>
                    <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                      Die {companyName} Cyberversicherung schützt Ihre Praxis vor allen relevanten Cyber-Risiken
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
                    {coverageAreas.map((area, index) => {
                      const Icon = area.icon;
                      return (
                        <GlowCard key={index} to="#" index={index}>
                          <div className="p-3 sm:p-4 lg:p-5">
                            <div className="flex flex-col items-center gap-2 sm:gap-3 mb-4">
                              <div className="flex items-center justify-center">
                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/70" />
                              </div>
                              <h3 className="text-lg sm:text-xl text-center text-white font-semibold">
                                {area.title}
                              </h3>
                            </div>
                            <ul className="space-y-2 sm:space-y-3">
                              {area.items.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                  <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </GlowCard>
                      );
                    })}
                  </div>
                </div>
              </ScrollAnimatedSection>
            </section>

            {/* Target Groups with InteractiveCard */}
            <section className="py-16 sm:py-20 lg:py-24">
              <ScrollAnimatedSection>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">
                      <span className="block text-foreground">Für wen ist die</span>
                      <span 
                        className="inline-block bg-clip-text text-transparent py-1"
                        style={{ 
                          backgroundImage: `linear-gradient(to right, ${companyColor}, ${companyColor}dd, ${companyColor})`,
                          textShadow: `0 0 40px ${companyColor}40, 0 0 80px ${companyColor}20`
                        }}
                      >
                        {companyName} Cyberversicherung
                      </span>
                      <span className="block text-foreground mt-3">geeignet?</span>
                    </h2>
                    <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mt-6">
                      Maßgeschneiderter Schutz für jede Praxisgröße
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                    {targetGroups.map((group, index) => {
                      const Icon = group.icon;
                      return (
                        <InteractiveCard 
                          key={index}
                          enableTilt={true}
                          enableDynamicShadow={true}
                          className="p-3 sm:p-4 lg:p-5 border-border/50 hover:border-accent/50"
                        >
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${companyColor}15` }}>
                              <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: companyColor }} />
                            </div>
                            <div>
                              <h3 className="text-lg sm:text-xl mb-2 font-semibold">{group.title}</h3>
                              <p className="text-xs sm:text-sm text-muted-foreground">{group.description}</p>
                            </div>
                          </div>
                          <ul className="space-y-2 mb-4">
                            {group.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="pt-3 border-t border-white/10">
                            <p className="text-xs sm:text-sm font-semibold" style={{ color: companyColor }}>
                              {group.recommendation}
                            </p>
                          </div>
                        </InteractiveCard>
                      );
                    })}
                  </div>
                </div>
              </ScrollAnimatedSection>
            </section>

            {/* Versicherungskonditionen with GlowCard */}
            <section className="py-16 sm:py-20 lg:py-24 relative">
              <div className="absolute inset-0 bg-gradient-to-b from-accent/[0.02] via-background to-accent/[0.02]" />
              <ScrollAnimatedSection>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                  <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                      Versicherungskonditionen im Überblick
                    </h2>
                    <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                      Transparente Bedingungen und flexible Gestaltungsmöglichkeiten
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
                    {insuranceConditions.map((condition, index) => {
                      const Icon = condition.icon;
                      return (
                        <GlowCard key={index} to="#" index={index}>
                          <div className="p-3 sm:p-4 lg:p-5">
                            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-2 sm:gap-3 mb-3">
                              <div className="flex items-center justify-center">
                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white/70" />
                              </div>
                              <h3 className="text-lg sm:text-xl text-center lg:text-left text-white font-semibold">
                                {condition.title}
                              </h3>
                            </div>
                            {Array.isArray(condition.description) ? (
                              <ul className="space-y-2">
                                {condition.description.map((item, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <span className="text-white/50 mt-1 flex-shrink-0">•</span>
                                    <span className="text-xs sm:text-sm text-white/70 leading-relaxed">{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs sm:text-sm text-white/70 leading-relaxed">{condition.description}</p>
                            )}
                          </div>
                        </GlowCard>
                      );
                    })}
                  </div>
                </div>
              </ScrollAnimatedSection>
            </section>

            {/* Timeline Section */}
            <TimelineSection companyName={companyName} companyColor={companyColor} />

            {/* FAQ Section */}
            <FAQSection questions={faqs} companyName={companyName} />

            {/* Document Downloads */}
            <DocumentDownloads documents={documents} companyName={companyName} />

            {/* Three Steps Protection */}
            <ThreeStepsProtection companyColor={companyColor} companyName={companyName} />

            {/* New CTA Section with ContactForm */}
            <section className="py-20 px-4 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/15 via-accent/5 to-accent/15" />
              <motion.div 
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent"
              />
              
              <div className="container mx-auto max-w-5xl relative z-10">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <ScrollAnimatedSection>
                    <h2 className="text-3xl font-bold mb-4">Jetzt persönlich beraten lassen</h2>
                    <p className="text-muted-foreground mb-6">
                      Unsere Experten helfen Ihnen, die {companyName} Cyberversicherung optimal auf Ihre Bedürfnisse abzustimmen.
                    </p>
                    <Link 
                      to="/termin-buchen"
                      className="inline-flex items-center px-8 py-4 bg-transparent text-white border-2 border-accent rounded-md font-semibold transition-all hover:shadow-[0_0_25px_rgba(139,195,74,0.5)]"
                    >
                      Jetzt Termin vereinbaren
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </ScrollAnimatedSection>
                  
                  <div className="bg-card/80 backdrop-blur-sm border border-accent/30 rounded-xl p-6 hover:border-accent/50 transition-colors">
                    <h3 className="text-xl font-semibold mb-4 text-center">Beratung anfragen</h3>
                    <ContactForm />
                  </div>
                </div>
              </div>
            </section>

            {/* Sticky CTA */}
            <StickyCTA companyName={companyName} companyColor={companyColor} />
          </main>

          <Footer />
    </>
  );
};
